import type { Niche, SizeTarget } from '@fluenceur/types';

/** Minimal translate signature so these helpers stay free of react-i18next types. */
type TranslateFn = (key: string) => string;

/**
 * Common markets shown alongside whatever came back from extraction. Shared by
 * the geo sub-step and the Settings preference editor so the option set stays a
 * single source of truth.
 */
export const COMMON_GEO = ['FR', 'BE', 'CH', 'US', 'GB', 'DE', 'IT', 'ES', 'CA'];

/**
 * Human label for a creator size bucket. The follower ranges are baked into the
 * i18n copy (companion.sizes.<value>) so the chip reads as a plain creator type.
 */
export const sizeTargetLabel = (size: SizeTarget | string, t: TranslateFn): string =>
  t(`companion.sizes.${size}`);

/** Human label for a niche taxonomy value (companion.niches.<value>). */
export const nicheLabel = (niche: Niche | string, t: TranslateFn): string =>
  t(`companion.niches.${niche}`);

/**
 * Localized country name for an ISO region code via `Intl.DisplayNames`. No
 * hand-written country map: the platform resolves the name for the active
 * locale. Falls back to the raw code if the runtime cannot resolve it.
 */
export const geoLabel = (code: string, locale: string): string => {
  try {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    return display.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};
